import { Component, Inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { BlindData, ModalBlindData, Opening } from '@root/app.interfaces';
import { STORAGE_NAMES } from '@root/app.config';
import { NgForOf, TitleCasePipe } from '@angular/common';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { ShareService } from '@core/services/share-data/share-data.service';
import { first } from 'rxjs/operators';
import { ConfirmPopoverComponent } from '@shared/modals/confirm-popover/confirm-popover.component';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from '@core/services/modal/modal.service';

@Component({
	selector: 'app-blind-list-modal',
	standalone: true,
	imports: [TranslateModule, TitleCasePipe, NgForOf, ConfirmPopoverComponent, NgbPopover],
	templateUrl: './blind-list-modal.component.html',
	styleUrls: ['./blind-list-modal.component.scss', './blind-list-modal.responsive.scss'],
})
export class BlindListModalComponent {
	static readonly ID = crypto.randomUUID();

	blindListData: BlindData[];
	modalBlindData: ModalBlindData[];
	placeholderBlinds: ModalBlindData[];
	openingBlindIds: { [key: string]: number };

	constructor(
		@Inject(MAT_LEGACY_DIALOG_DATA)
		public data: { opening: Opening; usedIds?: number[]; placeholderBlinds?: Opening[]; openingBlindIds?: { [key: string]: number } },
		private sessionStorageService: SessionStorageService,
		private shareDataService: ShareService,
		private modalService: ModalService,
		public dialogRef: MatDialogRef<BlindListModalComponent>,
	) {
		this.modalService.liftUpModal(this.dialogRef);

		this.openingBlindIds = this.data.openingBlindIds;
		this.getPlaceholderBlindData();
		this.getModalBlindData();
	}

	getPlaceholderBlindData() {
		if (this.data.placeholderBlinds) {
			const selectedOpeningId = this.data.opening.id;
			this.placeholderBlinds = this.data.placeholderBlinds
				.map((nb) => ({
					blindId: nb.blindId,
					displayNumber: nb.displayNumber,
					selected: nb.id === selectedOpeningId,
					type: nb.blindType,
					name: nb.name,
					openingId: nb.id, // I think you can not use this property if you generate blindId before
				}))
				.sort((a, b) => (a.displayNumber ?? 0) - (b.displayNumber ?? 0));
		}
	}

	getModalBlindData(): void {
		// Get a list of blinds. Select the active blind for the selected opening.
		const selectedBlindId = this.data.opening.blindId;
		this.blindListData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		this.modalBlindData = this.blindListData.reduce<ModalBlindData[]>((acc, b, index) => {
			// now a list of used blind ids can be passed in so the modal will only display available blinds
			if (!this.data.usedIds || !this.data.usedIds.includes(b.blind_id)) {
				acc.push({
					blindId: b.blind_id,
					displayNumber: index + 1,
					selected: b.blind_id === selectedBlindId,
					type: b.type,
					name: b.name,
					openingId: this.openingBlindIds?.[b.blind_id],
				});
			}
			return acc;
		}, []);

		if (this.placeholderBlinds) {
			this.modalBlindData = this.modalBlindData.concat(this.placeholderBlinds);
		}
	}

	onSelectBlind(blindData: ModalBlindData) {
		this.dialogRef.close(blindData); // Maybe should use onClose()?
	}

	onDeleteBlind(event: Event, blindData: ModalBlindData) {
		event?.stopImmediatePropagation();

		if (this.blindListData.length === 1 && this.modalBlindData.length > 1) {
			this.onDeleteLastCreateBlind(blindData);
			return;
		}

		this.shareDataService.getBlindUnselect.pipe(first()).subscribe(() => {
			this.reorderPlaceholderBlindData(-1);
			this.getModalBlindData();

			if (this.modalBlindData.length === 1) {
				this.onClose(); // Maybe should use onSelectBlind() for the last element?
			}
		});

		this.shareDataService.setBlindDelete(blindData.blindId);
	}

	onDeleteLastCreateBlind(blindData: ModalBlindData) {
		this.shareDataService.setOpeningDeselect(blindData.openingId);
		this.openingBlindIds[blindData.blindId] = this.placeholderBlinds[0].openingId;
		this.placeholderBlinds.shift();

		this.reorderPlaceholderBlindData(-1);
		this.getModalBlindData();

		if (this.modalBlindData.length === 1) {
			this.onClose();
		}
	}

	onDeletePlaceholderBlind(event: Event, blindData: ModalBlindData) {
		// Removing exactly no created blinds.
		event?.stopImmediatePropagation();

		// Added new Observer to Opening Overlay Component
		this.shareDataService.setOpeningDeselect(blindData.openingId);

		const deletedIndex = this.placeholderBlinds.findIndex((nb) => nb.openingId === blindData.openingId);
		this.reorderPlaceholderBlindData(deletedIndex);

		this.placeholderBlinds = this.placeholderBlinds.filter((nb) => nb.openingId !== blindData.openingId);
		this.getModalBlindData();

		if (this.modalBlindData.length === 1) {
			this.onClose(); // Maybe should use onSelectBlind() for the last element?
		}
	}

	reorderPlaceholderBlindData(deletedBlindIndex: number) {
		// Reorder elements in array according to reorderDisplayNumbers() function in Opening Overlay Component
		this.placeholderBlinds?.forEach((nb, index) => {
			nb.displayNumber = index > deletedBlindIndex ? nb.displayNumber - 1 : nb.displayNumber;
			nb.name = `Blind ${nb.displayNumber}`;
		});
	}

	onClose(): void {
		this.dialogRef.close();
	}
}
